import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useQuotes, QUOTE_STATUS_LABEL, QUOTE_STATUS_VARIANT } from "@/modules/quotes/api";
import { useOrders, ORDER_STATUS_LABEL, ORDER_STATUS_VARIANT } from "@/modules/orders/OrdersPage";
import { dateShort, money } from "@/lib/format";
import { EmptyState } from "@/components/shared/EmptyState";
import { useContracts } from "@/modules/contracts/api";
import { ContractsTable } from "@/modules/contracts/ContractsPage";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function ClientQuotesTab({ clientId }: { clientId: string }) {
  const nav = useNavigate();
  const { data: quotes } = useQuotes({ clientId });
  return (
    <div>
      <div className="mb-4 flex justify-end"><Button size="sm" onClick={() => nav(`/devis/nouveau?client=${clientId}`)}><Plus className="mr-2 h-4 w-4" />Nouveau devis</Button></div>
      {quotes && quotes.length === 0 ? <EmptyState title="Aucun devis" description="Le premier devis de ce client reprendra sa devise, son acompte, son délai et ses tarifs négociés." /> : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Objet</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>{quotes?.map((q) => (
              <TableRow key={q.id} className={`cursor-pointer ${q.superseded_by ? "opacity-60" : ""}`} onClick={() => nav(`/devis/${q.id}`)}>
                <TableCell className="font-mono text-xs">{q.number ?? "brouillon"}{q.version > 1 ? ` v${q.version}` : ""}</TableCell><TableCell>{q.title ?? "—"}</TableCell><TableCell>{dateShort(q.issue_date)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(q.total, q.currency)}</TableCell><TableCell><Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABEL[q.status]}</Badge></TableCell>
              </TableRow>))}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function ClientOrdersTab({ clientId }: { clientId: string }) {
  const nav = useNavigate();
  const { data: orders } = useOrders({ clientId });
  return orders && orders.length === 0 ? <EmptyState title="Aucune commande" description="Une commande se crée à partir d'un devis accepté." /> : (
    <div className="glass overflow-hidden rounded-2xl">
      <Table>
        <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Objet</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>{orders?.map((o) => (
          <TableRow key={o.id} className="cursor-pointer" onClick={() => nav(`/commandes/${o.id}`)}>
            <TableCell className="font-mono text-xs">{o.number}</TableCell><TableCell>{o.title ?? "—"}</TableCell><TableCell>{dateShort(o.ordered_at)}</TableCell>
            <TableCell className="text-right tabular-nums">{money(o.total, o.currency)}</TableCell><TableCell><Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge></TableCell>
          </TableRow>))}</TableBody>
      </Table>
    </div>
  );
}

export function ClientContractsTab({ clientId }: { clientId: string }) {
  const nav = useNavigate();
  const { data: contracts } = useContracts({ clientId });
  return contracts && contracts.length === 0 ? <EmptyState title="Aucun contrat" description="Un contrat se crée depuis une commande, ou depuis la page Contrats (contrat-cadre)." /> : contracts ? <ContractsTable contracts={contracts} onOpen={(id) => nav(`/contrats/${id}`)} /> : null;
}
